// Usage d'Elasticsearch, jour par jour : combien de requêtes, et en combien de
// temps. Les compteurs viennent de GET /api/metrics/search, alimenté côté serveur
// par un service de fond : le visiteur du site public n'attend jamais la mesure.
//
// IMPORTANT : passe par l'instance `axios` partagée (JAMAIS fetch brut) pour que
// l'interceptor Auth0AxiosBridge attache le Bearer admin.
import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";

export interface SearchUsageKindPoint {
	requests: number;
	failures: number;
	/** Durée moyenne par requête, en millisecondes. */
	averageMs: number;
	/** Durée de la requête la plus lente de la journée, en millisecondes. */
	maxMs: number;
}

export interface SearchUsagePoint {
	date: string;
	search: SearchUsageKindPoint;
	suggest: SearchUsageKindPoint;
}

export interface SearchUsage {
	days: SearchUsagePoint[];
	today: SearchUsagePoint;
}

const apiBase = (process.env.REACT_APP_API_URL ?? "").replace(/\/+$/, "");

const VIDE: SearchUsageKindPoint = { requests: 0, failures: 0, averageMs: 0, maxMs: 0 };

/** Une réponse partielle ne doit pas casser la page : chaque champ a un repli. */
const normalize = (point: Partial<SearchUsagePoint> | undefined, fallbackDate: string): SearchUsagePoint => ({
	date: point?.date ?? fallbackDate,
	search: { ...VIDE, ...(point?.search ?? {}) },
	suggest: { ...VIDE, ...(point?.suggest ?? {}) },
});

/**
 * Les N derniers jours d'usage de la recherche, rafraîchis toutes les 60 s.
 * `error` reste nul tant qu'un jeu de données a déjà été affiché : une coupure
 * passagère ne doit pas vider le tableau de bord.
 *
 * `enabled: false` désactive tout appel réseau : la section peut alors être
 * alimentée par des données déjà chargées ailleurs (page /search-metrics).
 */
export function useSearchUsage(days = 14, pollMs = 60000, enabled = true) {
	const [usage, setUsage] = useState<SearchUsage | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const mountedRef = useRef(true);
	const hasDataRef = useRef(false);

	const refresh = useCallback(async () => {
		try {
			const r = await axios.get<SearchUsage>(`${apiBase}/api/metrics/search?days=${days}`, {
				withCredentials: true,
			});
			if (!mountedRef.current) return;
			const today = new Date().toISOString().slice(0, 10);
			setUsage({
				days: (r.data?.days ?? []).map((point) => normalize(point, today)),
				today: normalize(r.data?.today, today),
			});
			hasDataRef.current = true;
			setError(null);
		} catch (err) {
			console.error(err);
			if (mountedRef.current && !hasDataRef.current) {
				setError("Les compteurs de la recherche n'ont pas pu être lus.");
			}
		} finally {
			if (mountedRef.current) setLoading(false);
		}
	}, [days]);

	useEffect(() => {
		if (!enabled) {
			setLoading(false);
			return;
		}
		mountedRef.current = true;
		void refresh();
		const timer = setInterval(() => void refresh(), pollMs);
		return () => {
			mountedRef.current = false;
			clearInterval(timer);
		};
	}, [refresh, pollMs, enabled]);

	return { usage, loading, error, refresh };
}
